import { 
    AudioInputDevice,
    App, 
    Client,
    ClientsState,
    DashboardFeature,
    DashboardRole,
    HealthcareProvider, 
    HealthcareProvidersState, 
    ProviderLicenseStatus,
    SessionRecordingClient,
    TimelineFileUploadOptions,
    Therapist, 
    TherapistsState,
    TherapySession,
    TherapySessionParticipant,
    TherapySessionsState,
    TherapySessionWaveformPeak,
    User,
} from '../interfaces';

import { getTimelineMediaContentType, getTimelineMediaError, timelineUploadCancelled } from '../timelineFileUpload';
import { auth, storage } from '../firebase';

import { 
    Nullable, 
    PresentedData, 
    PresentedView, 
    Presenter,
} from './Presentation';

import { getFunctions, httpsCallable } from 'firebase/functions';
import { deleteObject, ref, uploadBytes, uploadBytesResumable } from 'firebase/storage';

import { OrganisationPresenter } from './OrganisationPresenter';
import { ClientsPresenter } from './ClientsPresenter';
import { OrganisationView } from './OrganisationView';
import { ClientsView } from './ClientsView';
import { MenuOption } from './DashboardView';
import { analyticsLogEvent } from '../Analytics';
import { getAuth, getIdTokenResult, signOut } from 'firebase/auth';
import { HowToGuidesPresenter } from './HowToGuidesPresenter';
import { HowToGuidesView } from './HowToGuidesView';
import { SpeechNeedsPresenter } from './SpeechNeedsPresenter';
import { SpeechNeedsView } from './SpeechNeedsView';
import { MessagesPresenter } from './MessagesPresenter';
import { MessagesView } from './MessagesView';
import { ToolsPresenter } from './ToolsPresenter';
import { ToolsView } from './ToolsView';
import {
    getDashboardRole,
    hasDashboardFeature,
    isDashboardProviderHydrated,
    isRootOrSuperRole,
} from '../DashboardAccess';
import { agreementProviderIdFromLocation, isOrganisationRoute } from '../saas/DashboardMigrationReturn';
import { hasExplicitSessionParticipantAssociation } from '../sessionParticipants';

type SessionRecordingStatus = 'idle' | 'recording' | 'saving';
const SESSION_WAVEFORM_SAMPLE_INTERVAL_MS = 500;
const SESSION_WAVEFORM_MAX_PEAKS = 800;
const MAX_SESSION_PARTICIPANTS = 100;

type HealthcareProviderClientCountsResponse = {
    clientCountByProviderId?: Record<string, unknown>;
    licenseStatusByProviderId?: Record<string, unknown>;
};

export class DashboardPresenter implements Presenter {
    private app: App;
    private view: Nullable<PresentedView>;
    private user: Nullable<User>;
    private dashboardRole: DashboardRole = 'unauthenticated';
    private currentTherapist: Nullable<Therapist> = null;
    // A Root-initiated migration returns to the exact provider selected before Stripe Checkout.
    private healthcareProviderId: string = agreementProviderIdFromLocation();
    private healthcareProviders: HealthcareProvider[] = [];
    private clientCountByProviderId: Record<string, number> = {};
    private licenseStatusByProviderId: Record<string, ProviderLicenseStatus | undefined> = {};
    private healthcareProviderClientCountsRequest: Promise<void> | null = null;
    private howToGuidesPresenter!: HowToGuidesPresenter;
    private organisationPresenter!: OrganisationPresenter;
    private clientsPresenter!: ClientsPresenter;
    private speechNeedsPresenter!: SpeechNeedsPresenter;
    private messagesPresenter!: MessagesPresenter;
    private toolsPresenter!: ToolsPresenter;
    // Stripe returns owners directly to Organisation after hosted billing actions.
    private selectedMenuOptionId: string = isOrganisationRoute(window.location.pathname) ?
        'ORGANISATION' : 'HOW_TO_GUIDES';
    private lastTrackedSectionKey: string = '';
    private menuOptions: Array<MenuOption> = [];
    private sessionRecorder: MediaRecorder | null = null;
    private sessionRecordingStream: MediaStream | null = null;
    private sessionRecordingChunks: Blob[] = [];
    private sessionRecordingStartedAt: Date | null = null;
    private sessionRecordingParticipants: Array<TherapySessionParticipant> = [];
    private sessionRecordingProviderId: string = '';
    private sessionRecordingMimeType: string = '';
    private sessionRecordingStatus: SessionRecordingStatus = 'idle';
    private isStartingSessionRecording: boolean = false;
    private sessionRecordingDurationSeconds: number = 0;
    private sessionRecordingClinicalNotes: string = '';
    private sessionRecordingClinicalNotesAtStop: string = '';
    private isSessionRecordingClinicalNotesExpanded: boolean = false;
    private sessionRecordingTimerId: ReturnType<typeof setInterval> | null = null;
    private sessionWaveformAudioContext: AudioContext | null = null;
    private sessionWaveformSource: MediaStreamAudioSourceNode | null = null;
    private sessionWaveformAnalyser: AnalyserNode | null = null;
    private sessionWaveformData: Uint8Array | null = null;
    private sessionWaveformTimerId: ReturnType<typeof setInterval> | null = null;
    private sessionWaveformPeaks: TherapySessionWaveformPeak[] = [];
    private audioInputDevices: AudioInputDevice[] = [];
    private selectedAudioInputDeviceId: string = '';
    private isRefreshingAudioInputDevices: boolean = false;
    private recordingClients: Array<Client> = [];
    private recordingContextClient: Nullable<Client> = null;
    private recordingTherapists: Array<Therapist> = [];
    private recordingSessions: Array<TherapySession> = [];
    private healthcareProviderSwitchQueue: Promise<void> = Promise.resolve();

    private setupMenuOptions() {
        this.howToGuidesPresenter = new HowToGuidesPresenter({ app: this.app });
        this.organisationPresenter = new OrganisationPresenter({ app: this.app, healthcareProviderId: this.healthcareProviderId });
        this.clientsPresenter = new ClientsPresenter({
            app: this.app,
            onClientRecordChange: (client) => {
                this.recordingContextClient = client;
                this.safeUpdatePresentedData();
            },
            onImportSessionFile: (
                client: SessionRecordingClient,
                file: File,
                options: TimelineFileUploadOptions = {}
            ) => this.importSessionFile(client, file, options),
        });
        this.speechNeedsPresenter = new SpeechNeedsPresenter({ app: this.app, healthcareProviderId: this.healthcareProviderId });
        this.messagesPresenter = new MessagesPresenter({ app: this.app, healthcareProviderId: this.healthcareProviderId });
        this.toolsPresenter = new ToolsPresenter({ app: this.app, healthcareProviderId: this.healthcareProviderId });

        console.log('DASHBOARD_PRESENTER: setupMenuOptions() healthcareProviderId:', this.healthcareProviderId);

        this.menuOptions = [
            { 
                label: 'Getting started', 
                icon: {
                    normal: require('./assets/icons/get_started_normal.svg').default,
                    selected: require('./assets/icons/get_started_selected.svg').default,
                },
                id: 'HOW_TO_GUIDES',
                presenterAndView: {
                    presenter: this.howToGuidesPresenter, 
                    presentedViewClass: HowToGuidesView
                },
            },
            { 
                label: 'Organisation', 
                icon: {
                    normal: require('./assets/icons/organisation_normal.svg').default,
                    selected: require('./assets/icons/organisation_selected.svg').default,
                },
                id: 'ORGANISATION',
                presenterAndView: {
                    presenter: this.organisationPresenter, 
                    presentedViewClass: OrganisationView
                },
            },
            { 
                label: 'Clients', 
                icon: {
                    normal: require('./assets/icons/clients_normal.svg').default,
                    selected: require('./assets/icons/clients_selected.svg').default,
                },
                id: 'USERS', 
                presenterAndView: {
                    presenter: this.clientsPresenter, 
                    presentedViewClass: ClientsView
                },
                feature: 'clients',
            },
            { 
                label: 'Support areas', 
                icon: {
                    normal: require('./assets/icons/speech_needs_normal.svg').default,
                    selected: require('./assets/icons/speech_needs_selected.svg').default,
                },
                id: 'SPEECH_NEEDS', 
                presenterAndView: {
                    presenter: this.speechNeedsPresenter,
                    presentedViewClass: SpeechNeedsView
                }, 
            },
            { 
                label: 'Messaging',
                icon: {
                    normal: require('./assets/icons/messages_normal.svg').default,
                    selected: require('./assets/icons/messages_selected.svg').default,
                },
                id: 'MESSAGES',
                presenterAndView: {
                    presenter: this.messagesPresenter,
                    presentedViewClass: MessagesView
                },
                feature: 'messaging',
            },
            { 
                label: 'Tools', 
                icon: {
                    normal: require('./assets/icons/tools_normal.svg').default,
                    selected: require('./assets/icons/tools_selected.svg').default,
                },
                id: 'TOOLS_SUPER',
                presenterAndView: {
                    presenter: this.toolsPresenter,
                    presentedViewClass: ToolsView
                },
                feature: 'development',
                isDevelopment: true,
                rootOrSuperOnly: true,
            },
        ];
    }

    private getSelectedHealthcareProvider(): HealthcareProvider | undefined {
        return this.healthcareProviders.find((provider) => provider.id === this.healthcareProviderId);
    }

    // Every dashboard surface uses the same provider-role matrix decision.
    private canUseFeature(feature: DashboardFeature, provider = this.getSelectedHealthcareProvider()): boolean {
        return hasDashboardFeature(provider, this.dashboardRole, feature);
    }

    private getAccessibleMenuOptions(): Array<MenuOption> {
        const accessibleOptions = this.menuOptions.filter((option) =>
            (!option.feature || this.canUseFeature(option.feature)) &&
            (!option.rootOrSuperOnly || isRootOrSuperRole(this.dashboardRole))
        );

        if (!accessibleOptions.some((option) => option.id === this.selectedMenuOptionId)) {
            this.selectedMenuOptionId = accessibleOptions[0]?.id || '';
        }
        return accessibleOptions;
    }

    private canSelectHealthcareProvider(provider: HealthcareProvider): boolean {
        return isRootOrSuperRole(this.dashboardRole) &&
            hasDashboardFeature(provider, this.dashboardRole, 'provider_selector');
    }

    // Counts are loaded separately so cross-provider client records remain private.
    private refreshHealthcareProviderClientCounts(): Promise<void> {
        const authenticatedUserId = this.user?.authenticatedUser?.uid;
        const canLoadCounts = isRootOrSuperRole(this.dashboardRole) &&
            this.canUseFeature('provider_selector');
        if (!authenticatedUserId || !canLoadCounts) {
            if (Object.keys(this.clientCountByProviderId).length > 0 ||
                Object.keys(this.licenseStatusByProviderId).length > 0) {
                this.clientCountByProviderId = {};
                this.licenseStatusByProviderId = {};
                this.safeUpdatePresentedData();
            }
            return Promise.resolve();
        }
        if (this.healthcareProviderClientCountsRequest) {
            return this.healthcareProviderClientCountsRequest;
        }

        const request = (async () => {
            try {
                const getClientCounts = httpsCallable<undefined, HealthcareProviderClientCountsResponse>(
                    getFunctions(),
                    'getHealthcareProviderClientCounts'
                );
                const result = await getClientCounts();
                if (
                    authenticatedUserId !== this.user?.authenticatedUser?.uid ||
                    !isRootOrSuperRole(this.dashboardRole) ||
                    !this.canUseFeature('provider_selector')
                ) {
                    return;
                }

                const nextCounts: Record<string, number> = {};
                Object.entries(result.data.clientCountByProviderId || {}).forEach(([providerId, count]) => {
                    if (typeof count === 'number' && Number.isSafeInteger(count) && count >= 0) {
                        nextCounts[providerId] = count;
                    }
                });
                this.clientCountByProviderId = nextCounts;
                const nextLicenseStatuses: Record<string, ProviderLicenseStatus | undefined> = {};
                Object.entries(result.data.licenseStatusByProviderId || {}).forEach(([providerId, status]) => {
                    if (status === 'available' || status === 'exhausted' || status === 'overage') {
                        nextLicenseStatuses[providerId] = status;
                    }
                });
                this.licenseStatusByProviderId = nextLicenseStatuses;
                this.safeUpdatePresentedData();
            } catch (error) {
                console.error('DASHBOARD_PRESENTER: Could not load healthcare provider client counts:', error);
            }
        })();

        this.healthcareProviderClientCountsRequest = request;
        void request.finally(() => {
            if (this.healthcareProviderClientCountsRequest === request) {
                this.healthcareProviderClientCountsRequest = null;
            }
        });
        return request;
    }

    // Generate a stable storage object id for each recorded audio file.
    private createRecordingFileId(): string {
        if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
            return crypto.randomUUID();
        }

        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    }

    // Prefer compact browser-native audio formats while staying compatible with Safari.
    private getSupportedRecordingMimeType(): string {
        if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
            return '';
        }

        const supportedMimeType = [
            'audio/webm;codecs=opus',
            'audio/webm',
            'audio/mp4',
            'audio/ogg;codecs=opus',
        ].find((mimeType) => MediaRecorder.isTypeSupported(mimeType));

        return supportedMimeType || '';
    }

    // Match the storage filename extension to the browser's recorded content type.
    private getRecordingFileExtension(mimeType: string): string {
        if (mimeType.includes('mp4')) {
            return 'm4a';
        }

        if (mimeType.includes('ogg')) {
            return 'ogg';
        }

        return 'webm';
    }

    // Keep imported filenames storage-safe while preserving a useful extension.
    private sanitizeImportedSessionFileName(fileName: string): string {
        return fileName
            .trim()
            .replace(/[^a-zA-Z0-9._-]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 120) || 'session-media';
    }

    // Estimate imported media duration from browser metadata when available.
    private getImportedSessionFileDurationMs(file: File, contentType: string, signal?: AbortSignal): Promise<number | undefined> {
        return new Promise((resolve, reject) => {
            if (signal?.aborted) { reject(timelineUploadCancelled()); return; }
            const mediaElement = document.createElement(contentType.startsWith('video/') ? 'video' : 'audio');
            const objectUrl = URL.createObjectURL(file);
            const timeoutId = window.setTimeout(() => {
                cleanup();
                resolve(undefined);
            }, 5000);
            const cleanup = () => {
                window.clearTimeout(timeoutId);
                signal?.removeEventListener('abort', cancel);
                mediaElement.onloadedmetadata = null;
                mediaElement.onerror = null;
                mediaElement.removeAttribute('src');
                URL.revokeObjectURL(objectUrl);
            };

            const cancel = () => { cleanup(); reject(timelineUploadCancelled()); };
            signal?.addEventListener('abort', cancel);
            mediaElement.preload = 'metadata';
            mediaElement.onloadedmetadata = () => {
                const durationSeconds = mediaElement.duration;
                cleanup();
                resolve(Number.isFinite(durationSeconds) && durationSeconds > 0 ?
                    Math.round(durationSeconds * 1000) :
                    undefined);
            };
            mediaElement.onerror = () => {
                cleanup();
                resolve(undefined);
            };
            mediaElement.src = objectUrl;
        });
    }

    // Keep the global microphone selector aligned with browser devices.
    private async refreshAudioInputDevices() {
        if (!navigator.mediaDevices?.enumerateDevices) {
            this.audioInputDevices = [];
            this.selectedAudioInputDeviceId = '';
            this.updateAudioInputDeviceViews();
            return;
        }

        if (this.isRefreshingAudioInputDevices) {
            return;
        }

        this.isRefreshingAudioInputDevices = true;
        try {
            const mediaDevices = await navigator.mediaDevices.enumerateDevices();
            const audioInputDevices = mediaDevices
                .filter((device) => device.kind === 'audioinput' && !!device.deviceId && device.deviceId !== 'default')
                .map((device, index) => ({
                    deviceId: device.deviceId,
                    groupId: device.groupId,
                    label: device.label || `Microphone ${index + 1}`,
                }));

            this.audioInputDevices = audioInputDevices;
            if (
                this.selectedAudioInputDeviceId &&
                !audioInputDevices.some((device) => device.deviceId === this.selectedAudioInputDeviceId)
            ) {
                this.selectedAudioInputDeviceId = '';
            }

            this.updateAudioInputDeviceViews();
        } catch (error) {
            console.error('DASHBOARD_PRESENTER: refreshAudioInputDevices(): Error listing microphones:', error);
        } finally {
            this.isRefreshingAudioInputDevices = false;
        }
    }

    // Record with the selected microphone, or the browser default when none is chosen.
    private getSessionRecordingAudioConstraints(): boolean | MediaTrackConstraints {
        if (!this.selectedAudioInputDeviceId) {
            return true;
        }

        return {
            deviceId: { exact: this.selectedAudioInputDeviceId },
        };
    }

    // Publish microphone choices to the global recorder form.
    private updateAudioInputDeviceViews() {
        this.safeUpdatePresentedData();
    }

    // Store the dashboard user's chosen microphone for the next recording.
    private selectAudioInputDevice = (deviceId: string) => {
        this.selectedAudioInputDeviceId = deviceId;
        this.updateAudioInputDeviceViews();
    };

    // The live level preview already has permission; refresh labels without opening another stream.
    private refreshSessionAudioInputDevices = async () => {
        await this.refreshAudioInputDevices();
    };

    // Refresh labels and device availability when the OS/browser device list changes.
    private handleAudioInputDevicesChanged = () => {
        void this.refreshAudioInputDevices();
    };

    // Stop the dashboard duration ticker when recording pauses or ends.
    private clearSessionRecordingTimer() {
        if (this.sessionRecordingTimerId) {
            clearInterval(this.sessionRecordingTimerId);
            this.sessionRecordingTimerId = null;
        }
    }

    // Release the microphone tracks once recording no longer needs them.
    private stopSessionRecordingStream() {
        if (this.sessionRecordingStream) {
            this.sessionRecordingStream.getTracks().forEach((track) => track.stop());
            this.sessionRecordingStream = null;
        }
    }

    // Resolve a browser AudioContext constructor across Chromium and Safari.
    private getAudioContextConstructor(): typeof AudioContext | null {
        return window.AudioContext || (window as any).webkitAudioContext || null;
    }

    // Stop the lightweight waveform sampler used while recording.
    private stopSessionWaveformCapture() {
        if (this.sessionWaveformTimerId) {
            clearInterval(this.sessionWaveformTimerId);
            this.sessionWaveformTimerId = null;
        }

        if (this.sessionWaveformSource) {
            this.sessionWaveformSource.disconnect();
            this.sessionWaveformSource = null;
        }

        this.sessionWaveformAnalyser = null;
        this.sessionWaveformData = null;
        if (this.sessionWaveformAudioContext) {
            void this.sessionWaveformAudioContext.close().catch((error) => {
                console.error('DASHBOARD_PRESENTER: stopSessionWaveformCapture(): Error closing AudioContext:', error);
            });
            this.sessionWaveformAudioContext = null;
        }
    }

    // Capture compact amplitude peaks during recording so playback never has to re-download the whole audio file.
    private startSessionWaveformCapture(stream: MediaStream) {
        this.stopSessionWaveformCapture();
        this.sessionWaveformPeaks = [];

        try {
            const AudioContextConstructor = this.getAudioContextConstructor();
            if (!AudioContextConstructor) {
                return;
            }

            const audioContext = new AudioContextConstructor();
            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 1024;
            source.connect(analyser);

            this.sessionWaveformAudioContext = audioContext;
            this.sessionWaveformSource = source;
            this.sessionWaveformAnalyser = analyser;
            this.sessionWaveformData = new Uint8Array(analyser.fftSize);
            this.sessionWaveformTimerId = setInterval(() => {
                this.sampleSessionWaveformPeak();
            }, SESSION_WAVEFORM_SAMPLE_INTERVAL_MS);
        } catch (error) {
            console.error('DASHBOARD_PRESENTER: startSessionWaveformCapture(): Error starting waveform capture:', error);
            this.stopSessionWaveformCapture();
        }
    }

    // Sample the current microphone amplitude into a small peak record.
    private sampleSessionWaveformPeak() {
        const analyser = this.sessionWaveformAnalyser;
        const data = this.sessionWaveformData;
        const startedAt = this.sessionRecordingStartedAt;
        if (!analyser || !data || !startedAt) {
            return;
        }

        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let index = 0; index < data.length; index += 1) {
            const normalizedSample = (data[index] - 128) / 128;
            sum += normalizedSample * normalizedSample;
        }

        const rms = Math.sqrt(sum / data.length);
        const endMs = Math.max(0, Math.round(Date.now() - startedAt.getTime()));
        this.sessionWaveformPeaks.push({
            startMs: Math.max(0, endMs - SESSION_WAVEFORM_SAMPLE_INTERVAL_MS),
            endMs,
            amplitude: Math.min(1, Number((rms * 3).toFixed(4))),
        });
    }

    // Keep waveform metadata small enough for Firestore while preserving visible peaks.
    private compactSessionWaveformPeaks(durationMs: number): TherapySessionWaveformPeak[] {
        const peaks = this.sessionWaveformPeaks.filter((peak) => peak.endMs > peak.startMs);
        if (peaks.length === 0) {
            return [];
        }

        const groupSize = Math.max(1, Math.ceil(peaks.length / SESSION_WAVEFORM_MAX_PEAKS));
        const compactedPeaks: TherapySessionWaveformPeak[] = [];
        for (let index = 0; index < peaks.length; index += groupSize) {
            const group = peaks.slice(index, index + groupSize);
            compactedPeaks.push({
                startMs: group[0].startMs,
                endMs: Math.min(durationMs, group[group.length - 1].endMs),
                amplitude: Math.max(...group.map((peak) => peak.amplitude)),
            });
        }

        return compactedPeaks;
    }

    // Publish recorder state to the global dashboard shell.
    private updateSessionRecordingViews() {
        this.safeUpdatePresentedData();
    }

    // Open or close the notes editor in the persistent recording panel.
    private toggleSessionRecordingClinicalNotes = () => {
        if (this.sessionRecordingStatus === 'idle') {
            return;
        }

        this.isSessionRecordingClinicalNotesExpanded = !this.isSessionRecordingClinicalNotesExpanded;
        this.updateSessionRecordingViews();
    };

    // Keep the draft notes live in presenter state so they can be saved with the session.
    private updateSessionRecordingClinicalNotes = (clinicalNotes: string) => {
        if (this.sessionRecordingStatus !== 'recording') {
            return;
        }

        this.sessionRecordingClinicalNotes = clinicalNotes;
        this.updateSessionRecordingViews();
    };

    // Tick the visible recording duration while the MediaRecorder is active.
    private startSessionRecordingTimer() {
        this.clearSessionRecordingTimer();
        this.sessionRecordingTimerId = setInterval(() => {
            if (!this.sessionRecordingStartedAt) {
                return;
            }

            const elapsedMs = Date.now() - this.sessionRecordingStartedAt.getTime();
            this.sessionRecordingDurationSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
            this.updateSessionRecordingViews();
        }, 1000);
    }

    // Return all recorder state to its idle baseline after save or failure.
    private resetSessionRecording() {
        this.clearSessionRecordingTimer();
        this.stopSessionWaveformCapture();
        this.stopSessionRecordingStream();
        this.sessionRecorder = null;
        this.sessionRecordingChunks = [];
        this.sessionRecordingStartedAt = null;
        this.sessionRecordingParticipants = [];
        this.sessionRecordingProviderId = '';
        this.sessionRecordingMimeType = '';
        this.sessionRecordingStatus = 'idle';
        this.isStartingSessionRecording = false;
        this.sessionRecordingDurationSeconds = 0;
        this.sessionRecordingClinicalNotes = '';
        this.sessionRecordingClinicalNotesAtStop = '';
        this.isSessionRecordingClinicalNotesExpanded = false;
        this.sessionWaveformPeaks = [];
        this.updateSessionRecordingViews();
    }

    // Release the microphone immediately when provider access is revoked mid-recording.
    private reconcileSessionRecordingAccess() {
        if (this.sessionRecordingStatus !== 'recording' ||
            (this.canUseFeature('clients') && this.canUseFeature('audio_scribe_reports'))) {
            return;
        }

        const recorder = this.sessionRecorder;
        if (recorder) {
            // A revoked recording cannot enter the upload flow through a queued stop event.
            recorder.ondataavailable = null;
            recorder.onstop = null;
            try {
                if (recorder.state !== 'inactive') recorder.stop();
            } catch (error) {
                console.warn('DASHBOARD_PRESENTER: Could not stop revoked session recorder:', error);
            }
        }
        this.resetSessionRecording();
    }

    // Ask for microphone access and start collecting audio chunks.
    private async startSessionRecording(
        submittedParticipants: Array<TherapySessionParticipant>
    ): Promise<boolean> {
        if (!this.canUseFeature('clients') || !this.canUseFeature('audio_scribe_reports')) {
            throw new Error('Your role does not have access to audio scribe and reports.');
        }

        if (this.sessionRecordingStatus !== 'idle' || this.isStartingSessionRecording) {
            return false;
        }

        if (!this.healthcareProviderId) {
            throw new Error('Select a healthcare provider before recording a session.');
        }

        if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
            throw new Error('Audio recording is not supported in this browser.');
        }

        let stream: MediaStream | null = null;
        try {
            const providerId = this.healthcareProviderId;
            let participants = this.getValidatedRecordingParticipants(submittedParticipants);
            this.isStartingSessionRecording = true;
            stream = await navigator.mediaDevices.getUserMedia({
                audio: this.getSessionRecordingAudioConstraints(),
            });
            if (providerId !== this.healthcareProviderId ||
                !this.canUseFeature('clients') || !this.canUseFeature('audio_scribe_reports')) {
                throw new Error('Recording access changed during microphone setup. Please try again.');
            }
            participants = this.getValidatedRecordingParticipants(submittedParticipants);
            void this.refreshAudioInputDevices();
            const mimeType = this.getSupportedRecordingMimeType();
            const mediaRecorderOptions = mimeType ? { mimeType } : undefined;
            const recorder = mediaRecorderOptions ? new MediaRecorder(stream, mediaRecorderOptions) : new MediaRecorder(stream);

            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    this.sessionRecordingChunks.push(event.data);
                }
            };

            recorder.onstop = () => {
                void this.finishSessionRecording();
            };

            this.sessionRecorder = recorder;
            this.sessionRecordingStream = stream;
            this.sessionRecordingChunks = [];
            this.sessionRecordingStartedAt = new Date();
            this.sessionRecordingParticipants = participants;
            this.sessionRecordingProviderId = providerId;
            this.sessionRecordingMimeType = mimeType || recorder.mimeType;
            this.sessionRecordingStatus = 'recording';
            this.sessionRecordingDurationSeconds = 0;
            this.sessionRecordingClinicalNotes = '';
            this.sessionRecordingClinicalNotesAtStop = '';
            this.isSessionRecordingClinicalNotesExpanded = false;
            this.isStartingSessionRecording = false;
            this.startSessionWaveformCapture(stream);
            recorder.start();
            this.startSessionRecordingTimer();
            this.updateSessionRecordingViews();
            return true;
        } catch (error) {
            console.error('DASHBOARD_PRESENTER: startSessionRecording(): Error starting recording:', error);
            stream?.getTracks().forEach((track) => track.stop());
            this.resetSessionRecording();
            throw error instanceof Error ? error : new Error('Could not start audio recording.');
        }
    }

    // Transition the active recorder into the saving flow.
    private stopSessionRecording = () => {
        const recorder = this.sessionRecorder;
        if (!recorder || this.sessionRecordingStatus !== 'recording') {
            return;
        }

        try {
            this.sessionRecordingClinicalNotesAtStop = this.sessionRecordingClinicalNotes;
            this.sessionRecordingStatus = 'saving';
            this.clearSessionRecordingTimer();
            this.updateSessionRecordingViews();
            recorder.stop();
        } catch (error) {
            console.error('DASHBOARD_PRESENTER: stopSessionRecording(): Error stopping recording:', error);
            window.alert(error instanceof Error ? error.message : 'Could not stop audio recording.');
            this.resetSessionRecording();
        }
    }

    // Upload the stopped recording and create the Sessions document.
    private async finishSessionRecording() {
        const providerId = this.sessionRecordingProviderId;
        const participants = [...this.sessionRecordingParticipants];
        const startedAt = this.sessionRecordingStartedAt;
        const endedAt = new Date();
        const mimeType = this.sessionRecordingMimeType || this.sessionRecorder?.mimeType || 'audio/webm';
        const audioBlob = new Blob(this.sessionRecordingChunks, { type: mimeType });
        const durationMs = startedAt ? Math.max(0, endedAt.getTime() - startedAt.getTime()) : 0;
        const clinicalNotes = this.sessionRecordingClinicalNotesAtStop || this.sessionRecordingClinicalNotes;
        this.sampleSessionWaveformPeak();
        this.stopSessionWaveformCapture();
        const waveformPeaks = this.compactSessionWaveformPeaks(durationMs);

        this.stopSessionRecordingStream();

        if (!providerId || !startedAt || audioBlob.size === 0) {
            window.alert('The audio recording could not be saved.');
            this.resetSessionRecording();
            return;
        }

        const fileId = this.createRecordingFileId();
        const contentType = audioBlob.type || mimeType || 'audio/webm';
        const storagePath = `HealthcareProviders/${providerId}/Sessions/${fileId}.${this.getRecordingFileExtension(contentType)}`;
        let didUpload = false;
        const clientParticipants = participants.filter((participant) => participant.personType === 'client');
        const therapistParticipants = participants.filter((participant) => participant.personType === 'therapist');
        const primaryClient = clientParticipants[0];

        try {
            // Access may change while a recording is stopping or its upload is completing.
            const requireRecordingAccess = () => {
                if (providerId !== this.healthcareProviderId || !this.canUseFeature('clients') ||
                    !this.canUseFeature('audio_scribe_reports')) {
                    throw new Error('Recording access changed. The session recording could not be saved.');
                }
            };
            requireRecordingAccess();
            await uploadBytes(ref(storage, storagePath), audioBlob, { contentType });
            didUpload = true;
            requireRecordingAccess();
            await this.app.getData().addTherapySession({
                healthcareProviderId: providerId,
                providerId,
                clientId: primaryClient?.personId,
                clientName: primaryClient?.displayName,
                clientIds: clientParticipants.map((participant) => participant.personId),
                therapistIds: therapistParticipants.map((participant) => participant.personId),
                participants,
                date: startedAt,
                startedAt,
                endedAt,
                durationMs,
                storagePath,
                audioContentType: contentType,
                audioSizeBytes: audioBlob.size,
                source: 'microphone',
                recordedByUserId: this.user?.authenticatedUser?.uid,
                recordedByEmail: this.user?.authenticatedUser?.email || null,
                clinicalNotes,
                clinicalNotesUpdatedAt: clinicalNotes.trim() ? endedAt : undefined,
                clinicalNotesUpdatedByUserId: clinicalNotes.trim() ? this.user?.authenticatedUser?.uid : undefined,
                clinicalNotesUpdatedByEmail: clinicalNotes.trim() ? this.user?.authenticatedUser?.email || null : undefined,
                transcriptStatus: 'pending',
                waveformPeaks,
                waveformVersion: 1,
            });
        } catch (error) {
            console.error('DASHBOARD_PRESENTER: finishSessionRecording(): Error saving recording:', error);
            if (didUpload) {
                try {
                    await deleteObject(ref(storage, storagePath));
                } catch (deleteError) {
                    console.error('DASHBOARD_PRESENTER: finishSessionRecording(): Error deleting unsaved audio:', deleteError);
                }
            }
            window.alert(error instanceof Error ? error.message : 'Could not save the session recording.');
        } finally {
            this.resetSessionRecording();
        }
    }

    // Store an imported recording once; background transcription belongs to its saved session.
    private importSessionFile = async (
        client: SessionRecordingClient,
        file: File,
        options: TimelineFileUploadOptions = {}
    ): Promise<string> => {
        const providerId = this.healthcareProviderId;
        const userId = this.user?.authenticatedUser?.uid;
        const userEmail = this.user?.authenticatedUser?.email || null;
        const checkContext = () => {
            if (options.signal?.aborted) throw timelineUploadCancelled();
            if (providerId !== this.healthcareProviderId || userId !== this.user?.authenticatedUser?.uid) {
                throw new Error('The selected provider or account changed. Open the client and try again.');
            }
            if (!this.canUseFeature('clients') || !this.canUseFeature('audio_scribe_reports')) {
                throw new Error('Your role does not have access to audio scribe and reports.');
            }
        };
        checkContext();
        if (!providerId) throw new Error('Select a healthcare provider before importing a session.');
        if (!client?.clientId) throw new Error('Select a client before importing a session.');
        const validationError = file ? getTimelineMediaError(file) : 'Choose an audio or video file to import.';
        if (validationError) throw new Error(validationError);

        const contentType = getTimelineMediaContentType(file);
        const importedAt = new Date();
        const durationMs = await this.getImportedSessionFileDurationMs(file, contentType, options.signal);
        checkContext();
        const isVideoImport = contentType.startsWith('video/');
        const fileId = this.createRecordingFileId();
        const safeFileName = this.sanitizeImportedSessionFileName(file.name);
        const storagePath = `HealthcareProviders/${providerId}/${isVideoImport ? 'SessionImports' : 'Sessions'}/${fileId}-${safeFileName}`;
        let didUpload = false;

        try {
            // Observe the actual transfer and abort it when the dialog's cancellation is confirmed.
            const task = uploadBytesResumable(ref(storage, storagePath), file, { contentType });
            const cancel = () => { task.cancel(); };
            const unsubscribe = task.on('state_changed', (snapshot) => {
                options.onProgress?.(snapshot.totalBytes > 0 ? Math.round(snapshot.bytesTransferred / snapshot.totalBytes * 100) : 0);
            });
            options.signal?.addEventListener('abort', cancel);
            try {
                if (options.signal?.aborted) cancel();
                await task;
                didUpload = true;
            } finally {
                unsubscribe();
                options.signal?.removeEventListener('abort', cancel);
            }
            checkContext();
            options.onProgress?.(100);
            checkContext();
            // Once this write commits, completion wins a late close; do not delete media used by processing.
            return await this.app.getData().addTherapySession({
                healthcareProviderId: providerId,
                providerId,
                clientId: client.clientId,
                clientName: client.clientName,
                date: importedAt,
                durationMs,
                storagePath: isVideoImport ? undefined : storagePath,
                audioContentType: isVideoImport ? undefined : contentType,
                audioSizeBytes: isVideoImport ? undefined : file.size,
                source: 'import',
                originalFileName: file.name,
                originalMediaContentType: isVideoImport ? contentType : undefined,
                originalMediaSizeBytes: isVideoImport ? file.size : undefined,
                importMediaStoragePath: isVideoImport ? storagePath : undefined,
                importMediaContentType: isVideoImport ? contentType : undefined,
                importMediaSizeBytes: isVideoImport ? file.size : undefined,
                recordedByUserId: userId,
                recordedByEmail: userEmail,
                transcriptStatus: 'pending',
                waveformVersion: 1,
            }, { signal: options.signal });
        } catch (error) {
            // Remove only an uploaded file that never became a saved session.
            if (didUpload) {
                try {
                    await deleteObject(ref(storage, storagePath));
                } catch (deleteError) {
                    if ((deleteError as { code?: string }).code !== 'storage/object-not-found') {
                        throw new Error('The import could not finish and its uploaded file could not be removed. Please contact support.');
                    }
                }
            }
            if (options.signal?.aborted) throw timelineUploadCancelled();
            throw error;
        }
    };

    constructor(options: { app: App }) {
        const { app } = options;
        if (!app) {
            throw new Error('DashboardPresenter(): missing app parameter');
        }

        this.app = app;
        console.log(`DASHBOARD_PRESENTER: constructor()`);

        this.setupMenuOptions();
        this.observeUser();
        this.observeHealthcareProviders();
        this.observeCurrentTherapist();
        this.observeRecordingClients();
        this.observeRecordingTherapists();
        this.observeRecordingSessions();
        if (navigator.mediaDevices?.addEventListener) {
            navigator.mediaDevices.addEventListener('devicechange', this.handleAudioInputDevicesChanged);
        }
        void this.refreshAudioInputDevices();
    }

    private observeUser() {
        this.app.queryUser().observe({
            changed: (user: User) => {
                console.log('DASHBOARD_PRESENTER: queryUser().observe()', user);
                // A new authenticated session may legitimately revisit the same section.
                if (this.user?.authenticatedUser?.uid !== user.authenticatedUser?.uid) {
                    this.lastTrackedSectionKey = '';
                }
                this.user = user;
                this.safeUpdatePresentedData();
            }
        });
    }

    private observeHealthcareProviders() {
        console.log('DASHBOARD_PRESENTER: observeHealthcareProviders()');
        this.app.getData().queryHealthcareProviders().observe({
            changed: (healthcareProviders: HealthcareProvidersState) => {
                console.log('DASHBOARD_PRESENTER: queryHealthcareProviders().observe()', healthcareProviders);
                this.healthcareProviders = healthcareProviders.healthcareProviders;
                this.reconcileSessionRecordingAccess();

                const homeProviderId = this.currentTherapist?.healthcareProviderId;
                const selectedProvider = this.getSelectedHealthcareProvider();
                if (homeProviderId && this.healthcareProviderId !== homeProviderId &&
                    (!selectedProvider || !this.canSelectHealthcareProvider(selectedProvider))) {
                    // Return visiting platform users home when selector access is revoked.
                    void this.switchHealthcareProvider(homeProviderId, false);
                }
                this.safeUpdatePresentedData();
                void this.refreshHealthcareProviderClientCounts();
            }
        });
    }

    private observeCurrentTherapist() {
        this.app.getData().queryCurrentTherapist().observe({
            changed: (therapist: Therapist) => {
                console.log('DASHBOARD_PRESENTER: queryCurrentTherapist().observe()', therapist);
                if (!therapist?.id) {
                    // Clear platform authority immediately during logout/account changes.
                    this.currentTherapist = null;
                    this.dashboardRole = 'unauthenticated';
                    this.healthcareProviderId = '';
                    this.reconcileSessionRecordingAccess();
                    this.clientCountByProviderId = {};
                    this.licenseStatusByProviderId = {};
                    this.safeUpdatePresentedData();
                    return;
                }

                const previousProviderId = this.currentTherapist?.healthcareProviderId;
                this.currentTherapist = therapist;
                this.dashboardRole = getDashboardRole(therapist);
                this.reconcileSessionRecordingAccess();
                if (!isRootOrSuperRole(this.dashboardRole)) {
                    this.clientCountByProviderId = {};
                    this.licenseStatusByProviderId = {};
                }
                const selectedAnotherProvider = !!this.healthcareProviderId &&
                    this.healthcareProviderId !== therapist.healthcareProviderId;
                if (selectedAnotherProvider && !isRootOrSuperRole(this.dashboardRole)) {
                    // A demoted platform user must immediately return to their own provider.
                    void this.switchHealthcareProvider(therapist.healthcareProviderId, false);
                } else if (therapist.healthcareProviderId &&
                    (!this.healthcareProviderId || this.healthcareProviderId === previousProviderId)) {
                    // Login already authorised the home provider; avoid repeating claims and token refresh.
                    this.applyHealthcareProviderScope(therapist.healthcareProviderId);
                } else {
                    this.safeUpdatePresentedData();
                }
            }
        });
    }

    // Keep the global recorder's Client suggestions aligned with the selected provider.
    private observeRecordingClients() {
        this.app.getData().queryClients().observe({
            changed: (clients: ClientsState) => {
                this.recordingClients = clients.clients;
                this.safeUpdatePresentedData();
            },
        });
    }

    // Keep the global recorder's Team suggestions aligned with the provider directory.
    private observeRecordingTherapists() {
        this.app.getData().queryTherapistsInCurrentHealthcareProvider().observe({
            changed: (therapists: TherapistsState) => {
                this.recordingTherapists = therapists.therapists;
                this.safeUpdatePresentedData();
            },
        });
    }

    // Retain a provider-level path back to recordings that have no participant timeline.
    private observeRecordingSessions() {
        this.app.getData().queryTherapySessions().observe({
            changed: (sessions: TherapySessionsState) => {
                this.recordingSessions = sessions.sessions;
                this.safeUpdatePresentedData();
            },
        });
    }

    // Build a provider-scoped, deduplicated roster for the participant combobox.
    private getRecordingParticipantOptions(): Array<TherapySessionParticipant> {
        const participantsByKey = new Map<string, TherapySessionParticipant>();
        this.recordingClients
            .filter((client): client is Client & {id: string} =>
                !!client.id && client.providerId === this.healthcareProviderId
            )
            .forEach((client) => {
                const displayName = [client.givenName, client.familyName]
                    .filter(Boolean)
                    .join(' ')
                    .trim() || client.displayName?.trim() || client.email?.trim() || 'Unnamed client';
                const participant: TherapySessionParticipant = {
                    personId: client.id,
                    personType: 'client',
                    displayName,
                    userType: client.userType,
                };
                participantsByKey.set(`client:${client.id}`, participant);
            });

        this.recordingTherapists
            .filter((therapist) =>
                !!therapist.id && therapist.healthcareProviderId === this.healthcareProviderId
            )
            .forEach((therapist) => {
                const participant: TherapySessionParticipant = {
                    personId: therapist.id,
                    personType: 'therapist',
                    displayName: therapist.name?.trim() || therapist.email?.trim() || 'Unnamed team member',
                };
                participantsByKey.set(`therapist:${therapist.id}`, participant);
            });

        if (this.currentTherapist?.id &&
            this.currentTherapist.healthcareProviderId === this.healthcareProviderId) {
            participantsByKey.set(`therapist:${this.currentTherapist.id}`, {
                personId: this.currentTherapist.id,
                personType: 'therapist',
                displayName: this.currentTherapist.name?.trim() ||
                    this.currentTherapist.email?.trim() || 'Unnamed team member',
            });
        }

        return Array.from(participantsByKey.values()).sort((leftParticipant, rightParticipant) =>
            leftParticipant.displayName.localeCompare(rightParticipant.displayName, undefined, {sensitivity: 'base'}) ||
            leftParticipant.personType.localeCompare(rightParticipant.personType)
        );
    }

    // Re-resolve submitted people from the live roster so stale UI state cannot cross provider scope.
    private getValidatedRecordingParticipants(
        submittedParticipants: Array<TherapySessionParticipant>
    ): Array<TherapySessionParticipant> {
        // Refuse empty rosters before opening the recording microphone.
        if (!Array.isArray(submittedParticipants) || submittedParticipants.length === 0) {
            throw new Error('Select at least one Client or Team member before recording.');
        }
        if (submittedParticipants.length > MAX_SESSION_PARTICIPANTS) {
            throw new Error(`A session can contain at most ${MAX_SESSION_PARTICIPANTS} participants.`);
        }
        const currentParticipantsByKey = new Map(
            this.getRecordingParticipantOptions().map((participant) => [
                `${participant.personType}:${participant.personId}`,
                participant,
            ])
        );
        const validatedParticipants = new Map<string, TherapySessionParticipant>();

        submittedParticipants.forEach((participant) => {
            const participantKey = `${participant.personType}:${participant.personId}`;
            const currentParticipant = currentParticipantsByKey.get(participantKey);
            if (!currentParticipant) {
                throw new Error('One or more selected participants no longer belong to this healthcare provider.');
            }
            validatedParticipants.set(participantKey, currentParticipant);
        });

        return Array.from(validatedParticipants.values());
    }

    presentedData(): PresentedData {
        const selectedProvider = this.getSelectedHealthcareProvider();
        const loading = !isDashboardProviderHydrated(
            this.user?.authenticatedUser,
            this.currentTherapist,
            this.healthcareProviderId,
            selectedProvider
        );
        const accessibleMenuOptions = loading ? [] : this.getAccessibleMenuOptions();
        const selectedMenuOptionIndex = Math.max(
            0,
            accessibleMenuOptions.findIndex((option) => option.id === this.selectedMenuOptionId)
        );
        const selectableHealthcareProviders = this.healthcareProviders.filter((provider) =>
            this.canSelectHealthcareProvider(provider)
        );
        const recordingParticipantOptions = this.getRecordingParticipantOptions();
        const defaultRecordingParticipant = this.currentTherapist?.healthcareProviderId === this.healthcareProviderId ?
            recordingParticipantOptions.find((participant) =>
                participant.personType === 'therapist' && participant.personId === this.currentTherapist?.id
            ) : undefined;
        // Only preselect an open Client record from the active page and provider.
        const defaultRecordingClientParticipant = this.selectedMenuOptionId === 'USERS' &&
            this.recordingContextClient?.providerId === this.healthcareProviderId ?
            recordingParticipantOptions.find((participant) =>
                participant.personType === 'client' && participant.personId === this.recordingContextClient?.id
            ) : undefined;
        const unassignedRecordingSessions = this.recordingSessions.filter(
            (session) => !hasExplicitSessionParticipantAssociation(session)
        );
        const ret = {
            loading,
            user: this.user,
            healthcareProviderId: this.healthcareProviderId,
            healthcareProviders: this.healthcareProviders,
            selectableHealthcareProviders,
            canSelectHealthcareProvider: selectableHealthcareProviders.length > 0 &&
                this.canUseFeature('provider_selector') &&
                isRootOrSuperRole(this.dashboardRole),
            clientCountByProviderId: this.clientCountByProviderId,
            licenseStatusByProviderId: this.licenseStatusByProviderId,
            sessionRecording: {
                status: this.sessionRecordingStatus,
                durationSeconds: this.sessionRecordingDurationSeconds,
                clinicalNotes: this.sessionRecordingClinicalNotes,
                areClinicalNotesExpanded: this.isSessionRecordingClinicalNotesExpanded,
                participants: this.sessionRecordingParticipants,
            },
            canRecordSessions: this.canUseFeature('clients') && this.canUseFeature('audio_scribe_reports'),
            recordingParticipantOptions,
            defaultRecordingParticipant,
            defaultRecordingClientParticipant,
            audioInputDevices: this.audioInputDevices,
            selectedAudioInputDeviceId: this.selectedAudioInputDeviceId,
            unassignedRecordingSessions,
            onStartSessionRecording: (participants: Array<TherapySessionParticipant>) =>
                this.startSessionRecording(participants),
            onDeleteUnassignedSession: (session: TherapySession) =>
                this.app.getData().deleteTherapySession(session),
            onEnsureSessionWaveform: (sessionId: string) =>
                this.app.getData().ensureTherapySessionWaveform(sessionId),
            onStopSessionRecording: this.stopSessionRecording,
            onToggleSessionRecordingClinicalNotes: this.toggleSessionRecordingClinicalNotes,
            onSessionRecordingClinicalNotesChange: this.updateSessionRecordingClinicalNotes,
            onAudioInputDeviceChange: this.selectAudioInputDevice,
            onRefreshAudioInputDevices: this.refreshSessionAudioInputDevices,
            onHealthcareProviderChange: (healthcareProviderId: string) => {
                if (this.sessionRecordingStatus !== 'idle' || this.isStartingSessionRecording) {
                    window.alert('Finish the current recording action before changing healthcare provider.');
                    return;
                }
                void this.switchHealthcareProvider(healthcareProviderId, true);
            },
            onHealthcareProviderSelectorOpen: () => {
                void this.refreshHealthcareProviderClientCounts();
            },
            onLogout: async () => {
                if (this.sessionRecordingStatus !== 'idle' || this.isStartingSessionRecording) {
                    window.alert('Stop and save the current recording before logging out.');
                    return;
                }
                const confirmed = window.confirm('Are you sure you want to logout?');
                if (confirmed) {
                    await signOut(auth);
                }
            },
            menuOptions: accessibleMenuOptions,
            selectedMenuOptionIndex,
            onMenuOptionSelect: (optionId: string) => { 
                if (accessibleMenuOptions.some((option) => option.id === optionId)) {
                    this.selectedMenuOptionId = optionId;
                }
                this.safeUpdatePresentedData();
            },
        };
        return ret;
    }

    private switchHealthcareProvider(providerId: string, showError: boolean): Promise<void> {
        // Serialize claim changes so rapid selections cannot finish out of order.
        const switchOperation = this.healthcareProviderSwitchQueue.then(
            () => this.performHealthcareProviderSwitch(providerId, showError),
            () => this.performHealthcareProviderSwitch(providerId, showError)
        );
        this.healthcareProviderSwitchQueue = switchOperation.catch((error) => {
            console.error('DASHBOARD_PRESENTER: Unexpected provider-switch failure:', error);
        });
        return switchOperation;
    }

    private applyHealthcareProviderScope(providerId: string) {
        // Publish the provider to every presenter only after its token claim is verified.
        this.healthcareProviderId = providerId;
        this.organisationPresenter.setHealthcareProviderId(providerId);
        this.speechNeedsPresenter.setHealthcareProviderId(providerId);
        this.messagesPresenter.setHealthcareProviderId(providerId);
        this.toolsPresenter.setHealthcareProviderId(providerId);
        this.app.getData().setHealthcareProviderId(providerId);
        this.safeUpdatePresentedData();
    }

    private async recoverHealthcareProviderScope(
        requestedProviderId: string,
        previousProviderId: string
    ): Promise<'requested' | 'previous' | 'none'> {
        try {
            const currentUser = getAuth().currentUser;
            if (!currentUser) {
                return 'none';
            }
            const tokenResult = await getIdTokenResult(currentUser, true);
            const claimedProviderId = typeof tokenResult.claims.providerId === 'string' ?
                tokenResult.claims.providerId : '';
            if (claimedProviderId === previousProviderId) {
                return 'previous';
            }

            const claimedProvider = this.healthcareProviders.find(
                (provider) => provider.id === claimedProviderId
            );
            const isHomeProvider = claimedProviderId === this.currentTherapist?.healthcareProviderId;
            if (claimedProviderId === requestedProviderId &&
                (isHomeProvider || (!!claimedProvider && this.canSelectHealthcareProvider(claimedProvider)))) {
                this.applyHealthcareProviderScope(claimedProviderId);
                return 'requested';
            }
        } catch (recoveryError) {
            console.error('DASHBOARD_PRESENTER: Could not recover provider scope:', recoveryError);
        }
        return 'none';
    }

    private async performHealthcareProviderSwitch(providerId: string, showError: boolean) {
        console.log('DASHBOARD_PRESENTER: switchHealthcareProvider()', providerId);
        const previousProviderId = this.healthcareProviderId;
        const provider = this.healthcareProviders.find((candidateProvider) => candidateProvider.id === providerId);
        const isHomeProvider = providerId === this.currentTherapist?.healthcareProviderId;
        if (!isHomeProvider && (!provider || !this.canSelectHealthcareProvider(provider))) {
            if (showError) {
                window.alert('Your role cannot select that healthcare provider.');
            }
            return;
        }

        // Update claims before exposing another provider's data in the browser.
        try {
            const functions = getFunctions();
            const setUserClaims = httpsCallable(functions, 'setUserClaims');


            const userEmail = this.user?.authenticatedUser?.email;
            console.log('DASHBOARD_PRESENTER: switchHealthcareProvider() userEmail:', userEmail);
            const result = await setUserClaims({ providerId, email: userEmail });
            
            console.log('DASHBOARD_PRESENTER: Updated custom claims for provider change:', result.data);
            
            // Get fresh token to verify the claims
            const auth = getAuth();
            if (!auth.currentUser) {
                throw new Error('The dashboard user signed out during the provider change.');
            }
            // Force-refresh and verify the exact provider claim before publishing scoped data.
            const tokenResult = await getIdTokenResult(auth.currentUser, true);
            console.log(tokenResult.claims);
            if (tokenResult.claims.providerId !== providerId) {
                throw new Error('The refreshed Authentication token has the wrong healthcare provider.');
            }
            console.log('DASHBOARD_PRESENTER: Updated provider ID claim:', tokenResult.claims.providerId);
        } catch (error) {
            console.error('Error updating custom claims for provider change:', error);
            const recovery = await this.recoverHealthcareProviderScope(providerId, previousProviderId);
            if (recovery === 'requested') {
                return;
            }
            if (recovery === 'none') {
                // Unknown claims must not remain paired with provider-scoped browser data.
                this.applyHealthcareProviderScope('');
            }
            if (showError) {
                window.alert('Could not change healthcare provider. Please try again.');
            }
            return;
        }

        this.applyHealthcareProviderScope(providerId);
    }

    viewWillUpdate(view: PresentedView): void {
        this.view = view;
        this.trackVisibleSection();
    }

    viewWillNotUpdate(/*view: PresentedView*/) {
        this.view = null;
    }

    private safeUpdatePresentedData() {
        const data = this.presentedData();
        if (this.view) {
            this.view.updatePresentedData(data);
            this.trackVisibleSection();
        }
    }

    // Track internal section navigation only after the authorized dashboard is visible.
    private trackVisibleSection(): void {
        const userId = this.user?.authenticatedUser?.uid;
        if (!this.view || !userId || this.user?.dashboardRole === 'unauthenticated' ||
            !isDashboardProviderHydrated(
                this.user?.authenticatedUser,
                this.currentTherapist,
                this.healthcareProviderId,
                this.getSelectedHealthcareProvider()
            )) {
            return;
        }
        const selectedSection = this.getAccessibleMenuOptions().find(
            (option) => option.id === this.selectedMenuOptionId
        );
        if (!selectedSection) return;

        // Keep repeat data updates and React StrictMode remounts out of section counts.
        const sectionKey = JSON.stringify([userId, this.healthcareProviderId, selectedSection.id]);
        if (sectionKey === this.lastTrackedSectionKey) return;
        this.lastTrackedSectionKey = sectionKey;
        analyticsLogEvent('Dashboard Section Viewed', {
            section_id: selectedSection.id,
            dashboard_role: this.dashboardRole,
            healthcare_provider_id: this.healthcareProviderId,
        });
    }
};
